import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { clubApi, fmtEur } from "../../lib/clubApi";
import { useClubStats } from "./ClubLayout";

// Simplified sale flow (matches user's mockup):
// Phone input -> lookup member -> amount input -> save.
export default function ClubSale() {
    const { refresh } = useClubStats();
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [amount, setAmount] = useState("");
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        clubApi.listMembers({ limit: 1000 })
            .then(setMembers)
            .catch(() => toast.error("Errore caricamento soci"))
            .finally(() => setLoading(false));
    }, []);

    const cleanPhone = (v) => (v || "").replace(/\D+/g, "");
    const norm = (v) => (v || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const candidates = useMemo(() => {
        const s = query.trim();
        if (!s || selected) return [];
        const digits = cleanPhone(s);
        const words = norm(s).split(/\s+/).filter(Boolean);
        return members.filter((m) => {
            if (digits.length >= 3 && cleanPhone(m.phone).includes(digits)) return true;
            const name = norm(m.name);
            return words.length > 0 && words.every((w) => name.includes(w));
        }).slice(0, 6);
    }, [query, members, selected]);

    const matched = useMemo(() => {
        if (selected) return selected;
        if (candidates.length === 1) return candidates[0];
        const digits = cleanPhone(query);
        if (digits.length >= 3) return candidates.find((m) => cleanPhone(m.phone) === digits) || null;
        return null;
    }, [selected, candidates, query]);

    const onQueryChange = (e) => { setQuery(e.target.value); setSelected(null); };
    const pick = (m) => { setSelected(m); setQuery(`${m.name} · ${m.phone}`); };

    const save = async () => {
        if (!matched) {
            toast.error("Nessun socio trovato con questo nome o telefono");
            return;
        }
        const n = parseFloat(String(amount).replace(",", "."));
        if (!n || n <= 0) return toast.error("Importo non valido");
        setBusy(true);
        try {
            const r = await clubApi.registerSale({ member_id: matched.id, amount: n });
            toast.success(`Vendita registrata: ${fmtEur(r.sale.amount)} · ${r.member.name}`);
            setQuery("");
            setSelected(null);
            setAmount("");
            refresh();
            // Update local member list with new stats
            setMembers((list) => list.map((m) => (m.id === r.member.id ? r.member : m)));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="club-sale-page">
            <h2 className="font-display font-bold text-2xl text-stone-900">Registra vendita</h2>

            <label className="block space-y-1.5">
                <span className="text-base font-bold text-stone-900">Socio (nome, cognome o telefono)</span>
                <input
                    type="text"
                    value={query}
                    onChange={onQueryChange}
                    placeholder="Es. Rossi oppure 333…"
                    className="input-club-new placeholder:text-stone-400"
                    data-testid="sale-phone"
                    autoComplete="off"
                />
                {!selected && candidates.length > 0 && (
                    <ul className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100 overflow-hidden" data-testid="sale-candidates">
                        {candidates.map((m) => (
                            <li key={m.id}>
                                <button
                                    type="button"
                                    onClick={() => pick(m)}
                                    className="w-full text-left px-4 py-2.5 active:bg-stone-50 hover:bg-stone-50"
                                    data-testid={`sale-candidate-${m.id}`}
                                >
                                    <span className="block font-bold text-stone-900 truncate">{m.name}</span>
                                    <span className="block text-xs text-stone-500">{m.phone}{m.zone ? ` · ${m.zone}` : ""}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <span className="block text-sm text-stone-500" data-testid="sale-match-status">
                    {loading
                        ? "Caricamento soci…"
                        : matched
                            ? <>Trovato: <b>{matched.name}</b> · {matched.phone}</>
                            : query.trim() && candidates.length === 0
                                ? "Nessun socio trovato."
                                : "Scrivi il nome, il cognome o il telefono del socio."}
                </span>
            </label>

            <label className="block space-y-1.5">
                <span className="text-base font-bold text-stone-900">Importo vendita (€)</span>
                <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                    className="input-club-new tabular-nums text-lg"
                    data-testid="sale-amount"
                    placeholder="0,00"
                />
            </label>

            <button
                onClick={save}
                disabled={busy || !matched || !amount}
                className="w-full py-4 rounded-2xl bg-stone-900 text-white text-lg font-bold shadow-md active:scale-[0.99] disabled:opacity-50"
                data-testid="sale-save"
            >
                {busy ? (
                    <span className="inline-flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Salvo…</span>
                ) : "Registra vendita"}
            </button>
        </div>
    );
}
